import "../styles/admin-base.css";
import "../styles/venue-management.css";
import {
	Building2,
	CalendarCheck,
	MapPin,
	Users,
	DollarSign,
	Plus,
	PencilLine,
	Trash2,
	Eye,
	EyeOff,
	X,
	AlertTriangle,
	Image as ImageIcon,
	FileText,
	MoreHorizontal,
	RefreshCw,
	Search,
	Filter,
	ChevronLeft,
	ChevronRight,
	Percent,
	Tag,
	Grid,
	List,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../api/admin";
import { API_BASE_URL } from "../../src/config";
import { resolveBackendAssetUrl } from "../../src/utils/assetUrl";
import {
	ACCEPTED_IMAGE_EXTENSIONS,
	MAX_OUTPUT_IMAGE_FILE_SIZE_BYTES,
	compressImageForUpload,
	validateInitialImageFile,
} from "../../src/utils/imageUpload";

const VENUE_TYPE_OPTIONS = [
	{ value: "indoor", label: "Indoor" },
	{ value: "outdoor", label: "Outdoor" },
	{ value: "semi-indoor", label: "Semi-Indoor" },
	{ value: "other", label: "Other" },
];

const MAX_IMAGE_FILE_SIZE = 5 * 1024 * 1024; // 5 MB per image
const MAX_COMPRESSED_IMAGE_FILE_SIZE = MAX_OUTPUT_IMAGE_FILE_SIZE_BYTES;
const TABLE_PAGE_SIZE = 8;
const ACTION_MENU_GAP = 8;
const ACTION_MENU_VIEWPORT_PADDING = 12;
const ACTION_MENU_WIDTH = 188;
const ACTION_MENU_MIN_HEIGHT = 120;
const ACTION_MENU_PREFERRED_HEIGHT = 220;

const DEFAULT_ADMIN_ID = (() => {
	const envValue = process.env.REACT_APP_DEFAULT_ADMIN_ID;
	const parsed = envValue ? Number.parseInt(envValue, 10) : NaN;
	if (Number.isFinite(parsed) && parsed > 0) {
		return parsed;
	}
	return 1;
})();

function computeFixedMenuPosition(triggerElement, options = {}) {
	if (!triggerElement?.getBoundingClientRect) {
		return {
			top: ACTION_MENU_VIEWPORT_PADDING,
			left: ACTION_MENU_VIEWPORT_PADDING,
			maxHeight: ACTION_MENU_PREFERRED_HEIGHT,
		};
	}

	const rect = triggerElement.getBoundingClientRect();
	const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
	const menuWidth = options.menuWidth ?? ACTION_MENU_WIDTH;
	const gap = options.gap ?? ACTION_MENU_GAP;
	const padding = options.padding ?? ACTION_MENU_VIEWPORT_PADDING;
	const minHeight = options.minHeight ?? ACTION_MENU_MIN_HEIGHT;
	const preferredHeight = options.preferredHeight ?? ACTION_MENU_PREFERRED_HEIGHT;

	const safeLeft = Math.max(
		padding,
		Math.min(rect.right - menuWidth, viewportWidth - menuWidth - padding),
	);
	const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap - padding);
	const spaceAbove = Math.max(0, rect.top - gap - padding);
	const shouldOpenUp = spaceBelow < minHeight && spaceAbove > spaceBelow;
	const maxHeight = Math.max(minHeight, shouldOpenUp ? spaceAbove : spaceBelow);
	const desiredHeight = Math.min(preferredHeight, maxHeight);
	const safeTop = shouldOpenUp
		? Math.max(padding, rect.top - gap - desiredHeight)
		: Math.max(padding, Math.min(rect.bottom + gap, viewportHeight - desiredHeight - padding));

	return {
		top: safeTop,
		left: safeLeft,
		maxHeight,
	};
}

function resolveImageSrc(value) {
	if (!value) return null;
	return resolveBackendAssetUrl(value, { apiBaseUrl: API_BASE_URL });
}

export default function VenueManagement() {
	const [venues, setVenues] = useState([]);
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(null);
	const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 50 });
	const [analytics, setAnalytics] = useState({
		totalVenues: 0,
		activeVenues: 0,
		totalBookings: 0,
		activeBookings: 0,
	});

	const [dialogState, setDialogState] = useState({ mode: null, venue: null });
	const [formState, setFormState] = useState({
		name: "",
		type: "indoor",
		capacity: "",
		ratePerHour: "",
		price6hrs: "",
		price12hrs: "",
		price24hrs: "",
		discountPercentage: "0",
		discountMode: "preset",
		maxPax: "",
		amenities: "",
		shortDescription: "",
		detailedDescription: "",
		location: "",
		email: "",
		mobileNumber: "",
		link: "",
		isVisible: true,
		imageFiles: [],
		existingImages: [],
	});
	const [formErrors, setFormErrors] = useState({});
	const [dialogError, setDialogError] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isImageProcessing, setIsImageProcessing] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [imageInputKey, setImageInputKey] = useState(0);

	const [confirmDeleteState, setConfirmDeleteState] = useState({
		isOpen: false,
		venue: null,
	});
	const [openMenuVenueId, setOpenMenuVenueId] = useState(null);
	const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, maxHeight: ACTION_MENU_PREFERRED_HEIGHT });
	const [searchQuery, setSearchQuery] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [filterType, setFilterType] = useState("");
	const [filterVisibility, setFilterVisibility] = useState("");
	const [viewMode, setViewMode] = useState("table");
	const [currentPage, setCurrentPage] = useState(1);

	const activeFilterCount = [Boolean(filterType), Boolean(filterVisibility)].filter(Boolean).length;

	const isMountedRef = useRef(true);

	const toggleVenueMenu = useCallback((clickEvent, venueId) => {
		clickEvent.stopPropagation();
		if (openMenuVenueId === venueId) {
			setOpenMenuVenueId(null);
			return;
		}
		setMenuPosition(computeFixedMenuPosition(clickEvent.currentTarget));
		setOpenMenuVenueId(venueId);
	}, [openMenuVenueId]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest?.(".admin-row-actions")) {
				setOpenMenuVenueId(null);
			}
		};
		const handleViewportChange = () => setOpenMenuVenueId(null);

		document.addEventListener("click", handleClickOutside);
		window.addEventListener("scroll", handleViewportChange, true);
		window.addEventListener("resize", handleViewportChange, true);

		return () => {
			document.removeEventListener("click", handleClickOutside);
			window.removeEventListener("scroll", handleViewportChange, true);
			window.removeEventListener("resize", handleViewportChange, true);
		};
	}, []);

	const fetchVenues = useCallback(async () => {
		setLoading(true);
		setErrorMessage(null);
		try {
			const res = await api.listVenues({ pageSize: 50, isDeleted: false });
			if (!isMountedRef.current) return;

			const items = res.data.results || [];
			setVenues(items);
			setMeta(res.data.meta || { total: 0, page: 1, pageSize: 50 });

			const totalVenues = items.length;
			const activeVenues = items.filter((v) => v.isVisible).length;

			try {
				const analyticsRes = await api.getVenueBookingAnalytics();
				if (isMountedRef.current) {
					const stats = analyticsRes.data?.statistics ?? {};
					setAnalytics({
						totalVenues,
						activeVenues,
						totalBookings: Number(stats.totalBookings) || 0,
						activeBookings: (Number(stats.pendingCount) || 0) + (Number(stats.approvedCount) || 0),
					});
				}
			} catch {
				if (isMountedRef.current) {
					setAnalytics({
						totalVenues,
						activeVenues,
						totalBookings: 0,
						activeBookings: 0,
					});
				}
			}
		} catch (err) {
			if (!isMountedRef.current) return;
			console.error("Unable to load venues", err);
			setErrorMessage("Unable to load venues. Please try again.");
		} finally {
			if (isMountedRef.current) {
				setLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		fetchVenues();
	}, [fetchVenues]);

	const buildFormStateFromVenue = (venue) => {
		if (!venue) {
			return {
				name: "",
				type: "indoor",
				capacity: "",
				ratePerHour: "",
				price6hrs: "",
				price12hrs: "",
				price24hrs: "",
				discountPercentage: "0",
				discountMode: "preset",
				maxPax: "",
				amenities: "",
				shortDescription: "",
				detailedDescription: "",
				location: "",
				email: "",
				mobileNumber: "",
				link: "",
				isVisible: true,
				imageFiles: [],
				existingImages: [],
			};
		}

		const discountVal = venue.discountPercentage?.toString() || "0";
		const presetValues = ["0", "25", "50", "75", "100"];
		const isPreset = presetValues.includes(discountVal);

		return {
			name: venue.name || "",
			type: venue.type || "indoor",
			capacity: venue.capacity?.toString() || "",
			ratePerHour: venue.ratePerHour?.toString() || "",
			price6hrs: venue.price6hrs?.toString() || "",
			price12hrs: venue.price12hrs?.toString() || "",
			price24hrs: venue.price24hrs?.toString() || "",
			discountPercentage: discountVal,
			discountMode: isPreset ? "preset" : "custom",
			maxPax: venue.maxPax?.toString() || "",
			amenities: venue.amenities || "",
			shortDescription: venue.shortDescription || "",
			detailedDescription: venue.detailedDescription || "",
			location: venue.location || "",
			email: venue.email || "",
			mobileNumber: venue.mobileNumber || "",
			link: venue.link || "",
			isVisible: venue.isVisible !== false,
			imageFiles: [],
			existingImages: venue.images || [],
		};
	};

	const openCreateDialog = () => {
		setDialogState({ mode: "create", venue: null });
		setFormState(buildFormStateFromVenue(null));
		setFormErrors({});
		setDialogError(null);
		setImageInputKey((prev) => prev + 1);
	};

	const openEditDialog = async (venue) => {
		setDialogState({ mode: "edit", venue });
		setDialogError(null);
		setFormErrors({});
		setImageInputKey((prev) => prev + 1);

		// Fetch full venue details including images, amenities, contact info, etc.
		try {
			const res = await api.getVenue(venue.id);
			const fullVenue = res.data.venue;
			setFormState(buildFormStateFromVenue(fullVenue));
			setDialogState({ mode: "edit", venue: fullVenue });
		} catch (err) {
			console.error("Failed to fetch venue details:", err);
			setDialogError("Failed to load venue details. Please try again.");
			// Fallback to using the limited data we have
			setFormState(buildFormStateFromVenue(venue));
		}
	};

	const closeDialog = () => {
		setDialogState({ mode: null, venue: null });
		setFormState(buildFormStateFromVenue(null));
		setFormErrors({});
		setDialogError(null);
		setImageInputKey((prev) => prev + 1);
	};

	const handleFieldChange = (field) => (event) => {
		const value = event.target.value;
		setFormState((prev) => ({ ...prev, [field]: value }));
		if (formErrors[field]) {
			setFormErrors((prev) => ({ ...prev, [field]: null }));
		}
	};

	const handleCheckboxChange = (field) => (event) => {
		const checked = event.target.checked;
		setFormState((prev) => ({ ...prev, [field]: checked }));
	};

	const handleImageSelect = async (event) => {
		const files = Array.from(event.target.files || []);
		if (!files.length) {
			return;
		}

		const candidateFiles = [];
		const errors = [];

		for (const file of files) {
			const validationMessage = validateInitialImageFile(file, MAX_IMAGE_FILE_SIZE);
			if (validationMessage) {
				errors.push(`${file.name}: ${validationMessage}`);
				continue;
			}
			candidateFiles.push(file);
		}

		if (!candidateFiles.length) {
			setFormErrors((prev) => ({ ...prev, images: errors.join(", ") }));
			event.target.value = "";
			return;
		}

		setIsImageProcessing(true);
		setDialogError(null);

		const compressedFiles = [];
		for (const file of candidateFiles) {
			try {
				const compressed = await compressImageForUpload(file);
				compressedFiles.push(compressed);
			} catch (error) {
				errors.push(`${file.name}: ${error?.message || "Compression failed"}`);
			}
		}

		if (compressedFiles.length) {
			setFormState((prev) => ({
				...prev,
				imageFiles: [...prev.imageFiles, ...compressedFiles],
			}));
		}

		if (errors.length > 0) {
			setFormErrors((prev) => ({ ...prev, images: errors.join(", ") }));
		} else {
			setFormErrors((prev) => ({ ...prev, images: null }));
		}

		event.target.value = "";
		setIsImageProcessing(false);
	};

	const removeUploadedImage = (index) => {
		setFormState((prev) => ({
			...prev,
			imageFiles: prev.imageFiles.filter((_, i) => i !== index),
		}));
	};

	const removeExistingImage = async (imageId) => {
		if (!dialogState.venue) return;

		try {
			await api.deleteVenueImage(dialogState.venue.id, imageId);
			setFormState((prev) => ({
				...prev,
				existingImages: prev.existingImages.filter((img) => img.id !== imageId),
			}));
		} catch (err) {
			console.error("Failed to remove image", err);
			setDialogError("Failed to remove image. Please try again.");
		}
	};

	const validateForm = () => {
		const errors = {};

		if (!formState.name.trim()) {
			errors.name = "Venue name is required";
		}

		if (!formState.shortDescription.trim()) {
			errors.shortDescription = "Short description is required";
		}

		if (formState.capacity && formState.capacity.trim()) {
			const capacityNum = Number(formState.capacity);
			if (!Number.isFinite(capacityNum) || capacityNum < 0) {
				errors.capacity = "Capacity must be a valid number";
			}
		}

		const priceFields = [
			{ key: "price6hrs", label: "6 Hours Price" },
			{ key: "price12hrs", label: "12 Hours Price" },
			{ key: "price24hrs", label: "24 Hours Price" },
		];
		for (const { key, label } of priceFields) {
			if (formState[key] && formState[key].trim()) {
				const num = Number(formState[key]);
				if (!Number.isFinite(num) || num < 0) {
					errors[key] = `${label} must be a valid number ≥ 0`;
				}
			}
		}

		const discountNum = Number(formState.discountPercentage);
		if (!Number.isFinite(discountNum) || discountNum < 0 || discountNum > 100) {
			errors.discountPercentage = "Discount must be between 0 and 100";
		}

		if (formState.maxPax && formState.maxPax.trim()) {
			const maxPaxNum = Number(formState.maxPax);
			if (!Number.isFinite(maxPaxNum) || maxPaxNum < 1) {
				errors.maxPax = "Max pax must be at least 1";
			}
		}

		if (
			dialogState.mode === "create" &&
			formState.imageFiles.length === 0
		) {
			errors.images = "At least one venue image is required";
		}

		if (formState.imageFiles.some((file) => file.size > MAX_COMPRESSED_IMAGE_FILE_SIZE)) {
			errors.images = "Each uploaded image must be 1MB or smaller after compression.";
		}

		if (isImageProcessing) {
			errors.images = "Images are still compressing. Please wait.";
		}

		return errors;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (isImageProcessing) {
			setDialogError("Images are still compressing. Please wait before saving.");
			return;
		}

		const errors = validateForm();
		setFormErrors(errors);

		if (Object.keys(errors).length > 0) {
			return;
		}

		setIsSubmitting(true);
		setDialogError(null);

		try {
			const formData = new FormData();
			formData.append("name", formState.name.trim());
			formData.append("type", formState.type);
			formData.append("capacity", formState.capacity.trim() || "0");
			formData.append("ratePerHour", formState.ratePerHour.trim() || "0");
			formData.append("price6hrs", formState.price6hrs.trim() || "0");
			formData.append("price12hrs", formState.price12hrs.trim() || "0");
			formData.append("price24hrs", formState.price24hrs.trim() || "0");
			formData.append("discountPercentage", formState.discountPercentage.trim() || "0");
			formData.append("maxPax", formState.maxPax.trim() || "0");
			formData.append("amenities", formState.amenities.trim());
			formData.append("shortDescription", formState.shortDescription.trim());
			formData.append("detailedDescription", formState.detailedDescription.trim());
			formData.append("location", formState.location.trim());
			formData.append("email", formState.email.trim());
			formData.append("mobileNumber", formState.mobileNumber.trim());
			formData.append("link", formState.link.trim());
			formData.append("isVisible", formState.isVisible ? "1" : "0");
			formData.append("adminId", DEFAULT_ADMIN_ID.toString());

			for (const file of formState.imageFiles) {
				formData.append("venueImages", file);
			}

			if (dialogState.mode === "edit") {
				await api.updateVenue(dialogState.venue.id, formData);
			} else {
				await api.createVenue(formData);
			}

			await fetchVenues();
			closeDialog();
		} catch (err) {
			console.error("Unable to save venue", err);
			const message =
				err.response?.data?.message ||
				err.response?.data?.errorMessage ||
				"Unable to save venue. Please try again.";
			setDialogError(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	const openDeleteConfirm = () => {
		setConfirmDeleteState({ isOpen: true, venue: dialogState.venue });
	};

	const closeDeleteConfirm = () => {
		setConfirmDeleteState({ isOpen: false, venue: null });
	};

	const handleDelete = async () => {
		if (!confirmDeleteState.venue) return;

		setIsDeleting(true);
		setDialogError(null);

		try {
			await api.deleteVenue(confirmDeleteState.venue.id);
			await fetchVenues();
			closeDeleteConfirm();
			closeDialog();
		} catch (err) {
			console.error("Unable to delete venue", err);
			const message =
				err.response?.data?.message || "Unable to delete venue. Please try again.";
			setDialogError(message);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleToggleVisibility = async (venue) => {
		try {
			await api.toggleVenueVisibility(venue.id, { isVisible: !venue.isVisible });
			await fetchVenues();
		} catch (err) {
			console.error("Unable to toggle visibility", err);
		}
	};

	const renderVisibilityChip = (venue) => {
		if (venue.isVisible) {
			return <span className="admin-chip is-blue">Visible</span>;
		}
		return <span className="admin-chip">Hidden</span>;
	};

	const filteredVenues = useMemo(() => {
		const normalizedQuery = searchQuery.trim().toLowerCase();
		return venues.filter((venue) => {
			if (filterType && venue?.type !== filterType) {
				return false;
			}

			if (filterVisibility) {
				const expectedVisible = filterVisibility === "visible";
				if (Boolean(venue?.isVisible) !== expectedVisible) {
					return false;
				}
			}

			if (!normalizedQuery) {
				return true;
			}

			const searchableValues = [
				venue?.name,
				venue?.type,
				venue?.location,
				venue?.shortDescription,
				venue?.isVisible ? "visible" : "hidden",
			];

			return searchableValues.some((value) =>
				String(value ?? "").toLowerCase().includes(normalizedQuery),
			);
		});
	}, [filterType, filterVisibility, searchQuery, venues]);

	const totalPages = Math.max(1, Math.ceil(filteredVenues.length / TABLE_PAGE_SIZE));

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	const paginatedVenues = useMemo(() => {
		const startIndex = (currentPage - 1) * TABLE_PAGE_SIZE;
		return filteredVenues.slice(startIndex, startIndex + TABLE_PAGE_SIZE);
	}, [currentPage, filteredVenues]);

	const handleSearchChange = (event) => {
		setSearchQuery(event.target.value);
		setCurrentPage(1);
	};

	const hasActiveFilters = Boolean(filterType || filterVisibility || searchQuery.trim());

	const handleClearFilters = () => {
		setFilterType("");
		setFilterVisibility("");
		setSearchQuery("");
		setCurrentPage(1);
	};

	const handlePreviousPage = () => {
		setCurrentPage((previous) => Math.max(1, previous - 1));
	};

	const handleNextPage = () => {
		setCurrentPage((previous) => Math.min(totalPages, previous + 1));
	};

	return (
		<div className="admin-page venue-management-page">
			<div className="admin-stack-lg">
				<header className="admin-surface venue-hero">
					<div className="venue-hero__intro">
						<span className="venue-hero__eyebrow">Venue Operations</span>
						<h1>Venue Management</h1>
						<p className="admin-muted">
							Manage venue listings, images, and availability
						</p>
					</div>
					<div className="admin-actions venue-hero__actions">
						<button
							type="button"
							className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
							onClick={fetchVenues}
							disabled={loading}
							aria-label="Refresh venues"
							title="Refresh venues"
						>
							<RefreshCw size={18} />
						</button>
						<button
							type="button"
							className="admin-button is-primary"
							onClick={openCreateDialog}
						>
							<Plus size={20} />
							Create Venue
						</button>
					</div>
				</header>

				<div className="admin-card-grid cols-4 venue-stats-grid venue-overview">
					<article className="admin-card">
						<div className="admin-card__label">Total Venues</div>
						<div className="admin-row">
							<div className="admin-card__value">{analytics.totalVenues}</div>
							<span className="admin-chip is-blue"><Building2 size={18} /></span>
						</div>
						<div className="admin-card__meta">All registered venues</div>
					</article>
					<article className="admin-card">
						<div className="admin-card__label">Active Venues</div>
						<div className="admin-row">
							<div className="admin-card__value">{analytics.activeVenues}</div>
							<span className="admin-chip is-green"><Eye size={18} /></span>
						</div>
						<div className="admin-card__meta">Visible to users</div>
					</article>
					<article className="admin-card">
						<div className="admin-card__label">Total Bookings</div>
						<div className="admin-row">
							<div className="admin-card__value">{analytics.totalBookings}</div>
							<span className="admin-chip is-info"><Users size={18} /></span>
						</div>
						<div className="admin-card__meta">Across all venues</div>
					</article>
					<article className="admin-card">
						<div className="admin-card__label">Active Bookings</div>
						<div className="admin-row">
							<div className="admin-card__value">{analytics.activeBookings}</div>
							<span className="admin-chip is-orange"><CalendarCheck size={18} /></span>
						</div>
						<div className="admin-card__meta">Pending + approved</div>
					</article>
				</div>

				{errorMessage && (
					<div className="venue-error-banner">
						{errorMessage}
					</div>
				)}

				<div className="admin-surface venue-search-surface venue-toolbar-surface">
					<div className="admin-row members-table__search-row venue-search-row">
						<div className="venue-search-field">
							<span className="admin-card__label">Search Venues</span>
							<div className="admin-card venue-search-card">
								<Search size={18} color="#64748b" />
								<input
									type="text"
									value={searchQuery}
									onChange={handleSearchChange}
									placeholder="Search by venue name, type, location, or visibility"
									className="venue-search-input"
								/>
							</div>
						</div>
						<div className="id-card-queue__filter-button-wrap venue-filter-toggle-wrap">
							<div className="admin-view-toggle">
								<button
									type="button"
									className={`admin-view-toggle__btn ${viewMode === "table" ? "is-active" : ""}`}
									onClick={() => setViewMode("table")}
									aria-label="Table View"
									title="Table View"
								>
									<List size={18} />
								</button>
								<button
									type="button"
									className={`admin-view-toggle__btn ${viewMode === "card" ? "is-active" : ""}`}
									onClick={() => setViewMode("card")}
									aria-label="Card View"
									title="Card View"
								>
									<Grid size={18} />
								</button>
							</div>
							<button
								type="button"
								className={`admin-button admin-button--ghost venue-filter-toggle ${showFilters ? "is-open" : ""} ${activeFilterCount > 0 ? "is-active" : ""}`}
								onClick={() => setShowFilters((open) => !open)}
								aria-pressed={showFilters}
							>
								<Filter size={18} /> Filters
								{activeFilterCount > 0 ? (
									<span className="venue-filter-toggle__badge">{activeFilterCount}</span>
								) : null}
							</button>
						</div>
					</div>

					{showFilters ? (
						<div className="admin-row admin-row--filters-inline venue-filters-inline">
							<select
								className="admin-select"
								value={filterType}
								onChange={(event) => {
									setFilterType(event.target.value);
									setCurrentPage(1);
								}}
							>
								<option value="">Type (All)</option>
								{VENUE_TYPE_OPTIONS.map((typeOption) => (
									<option key={typeOption.value} value={typeOption.value}>
										{typeOption.label}
									</option>
								))}
							</select>
							<select
								className="admin-select"
								value={filterVisibility}
								onChange={(event) => {
									setFilterVisibility(event.target.value);
									setCurrentPage(1);
								}}
							>
								<option value="">Visibility (All)</option>
								<option value="visible">Visible</option>
								<option value="hidden">Hidden</option>
							</select>
							{hasActiveFilters ? (
								<button
									type="button"
									className="admin-button admin-button--secondary"
									onClick={handleClearFilters}
								>
									Clear filters
								</button>
							) : null}
						</div>
					) : null}
				</div>

				{loading ? (
					<div className="admin-surface venue-state-box">
						<p className="admin-muted">Loading venues...</p>
					</div>
				) : venues.length === 0 ? (
					<div className="admin-surface venue-state-box">
						<p className="admin-muted">No venues found. Create your first venue!</p>
					</div>
				) : filteredVenues.length === 0 ? (
					<div className="admin-surface venue-state-box">
						<p className="admin-muted">No venues match your search query.</p>
					</div>
				) : (
					<div className="admin-surface venue-table-surface">
						<div className={`admin-view-transition ${viewMode === "card" ? "is-card-view" : "is-table-view"}`}>
							{viewMode === "card" ? (
								<div className="admin-card-grid venue-card-grid">
									{paginatedVenues.map((venue) => (
										<div className="admin-card venue-card" key={venue.id}>
											<div className="venue-card__image-wrap">
												{venue.featuredImage ? (
													<img
														src={resolveImageSrc(venue.featuredImage)}
														alt={venue.name}
														loading="lazy"
														className="venue-card__image"
													/>
												) : (
													<div className="admin-image-fallback venue-card__image-fallback">
														<ImageIcon size={24} className="admin-muted" />
													</div>
												)}
												<div className="venue-card__badges">
													<span className="ev-badge">{venue.type}</span>
												</div>
											</div>
											<div className="venue-card__content">
												<h4 className="venue-card__title" title={venue.name}>{venue.name}</h4>
												
												<div className="venue-card__meta-inline">
													<span title={venue.location}>
														<MapPin size={12} /> {venue.location || "Location TBD"}
													</span>
													<span>
														<Users size={12} /> {venue.capacity} Max
													</span>
													<span>
														<DollarSign size={12} />
														{Number(venue.discountPercentage || 0) > 0 ? (
															<>
																<span className="admin-text-strong">₱{(Number(venue.price6hrs || 0) * (1 - Number(venue.discountPercentage) / 100)).toLocaleString("en-PH")}</span>
																<span className="venue-table-original-price" style={{ fontSize: '10px', marginLeft: '4px' }}>₱{Number(venue.price6hrs || 0).toLocaleString("en-PH")}</span>
															</>
														) : (
															<span className="admin-text-strong">₱{Number(venue.price6hrs || 0).toLocaleString("en-PH")}</span>
														)}
													</span>
												</div>
												<p className="venue-card__desc">{venue.shortDescription?.slice(0, 80)}{venue.shortDescription?.length > 80 ? "..." : ""}</p>
											</div>
											<div className="venue-card__footer">
												<button
													type="button"
													className="admin-button admin-button--secondary venue-card__btn-link"
													onClick={() => {
														openEditDialog(venue);
													}}
												>
													<PencilLine size={14} /> Edit
												</button>
												<div className="venue-card__actions">
													<div className="admin-row-actions">
														<button
															type="button"
															className="admin-icon-button"
															onClick={(clickEvent) => toggleVenueMenu(clickEvent, venue.id)}
															aria-haspopup="menu"
															aria-expanded={openMenuVenueId === venue.id}
															title="More actions"
														>
															<MoreHorizontal size={16} />
														</button>
														{openMenuVenueId === venue.id ? (
															<div
																className="admin-menu admin-menu--fixed"
																style={{
																	"--admin-menu-top": `${menuPosition.top}px`,
																	"--admin-menu-left": `${menuPosition.left}px`,
																	"--admin-menu-max-height": `${menuPosition.maxHeight}px`,
																}}
															>
																<button
																	type="button"
																	onClick={(menuEvent) => {
																		menuEvent.stopPropagation();
																		setOpenMenuVenueId(null);
																		handleToggleVisibility(venue);
																	}}
																>
																	{venue.isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
																	{venue.isVisible ? "Hide" : "Show"}
																</button>
																<button
																	type="button"
																	onClick={(menuEvent) => {
																		menuEvent.stopPropagation();
																		setOpenMenuVenueId(null);
																		setDialogState({ mode: "edit", venue });
																		setConfirmDeleteState({ isOpen: true, venue });
																	}}
																	className="venue-menu-item-danger"
																>
																	<Trash2 size={14} />
																	Delete
																</button>
															</div>
														) : null}
													</div>
												</div>
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="venue-table-wrapper admin-table-wrapper admin-scroll">
									<table className="admin-table venue-table">
										<thead>
											<tr>
												<th className="venue-col-image">Image</th>
												<th className="venue-col-name">Name & Description</th>
												<th className="venue-col-type">Type & Capacity</th>
												<th className="venue-col-rate">Starting Price</th>
												<th className="venue-col-location">Location</th>
												<th className="venue-col-visible">Visibility</th>
												<th className="venue-col-actions">Actions</th>
											</tr>
										</thead>
										<tbody>
											{paginatedVenues.map((venue) => (
												<tr key={venue.id} className="admin-table-row-enhanced">
													<td>
														<div className="admin-events-preview__image venue-thumbnail-lg">
															{venue.featuredImage ? (
																<img
																	src={resolveImageSrc(venue.featuredImage)}
																	alt={venue.name}
																	loading="lazy"
																/>
															) : (
																<div className="admin-image-fallback">
																	<ImageIcon size={24} className="admin-muted" />
																</div>
															)}
														</div>
													</td>
													<td>
														<span className="admin-text-strong">{venue.name}</span>
														<p className="venue-table-secondary admin-text-muted">
															{venue.shortDescription?.slice(0, 80)}
															{venue.shortDescription?.length > 80 ? "..." : ""}
														</p>
													</td>
													<td>
														<span className="admin-pill is-soft">{venue.type}</span>
														<p className="venue-table-secondary admin-text-muted" style={{ marginTop: '6px' }}>
															<Users size={14} className="venue-icon-inline" /> {venue.capacity} guests
														</p>
													</td>
													<td>
														{Number(venue.discountPercentage || 0) > 0 ? (
															<>
																<span className="venue-table-original-price admin-text-muted">₱{Number(venue.price6hrs || 0).toLocaleString("en-PH")}</span>
																<br />
																<strong className="admin-text-strong">₱{(Number(venue.price6hrs || 0) * (1 - Number(venue.discountPercentage) / 100)).toLocaleString("en-PH")}</strong>
																<span className="admin-chip is-green venue-discount-chip">{venue.discountPercentage}% OFF</span>
															</>
														) : (
															<><strong className="admin-text-strong">₱{Number(venue.price6hrs || 0).toLocaleString("en-PH")}</strong><p className="venue-table-secondary admin-text-muted">6hrs pkg</p></>
														)}
													</td>
													<td>
														<p className="venue-table-secondary">
															{venue.location || "—"}
														</p>
													</td>
													<td>{renderVisibilityChip(venue)}</td>
													<td>
														<div className="admin-row-actions">
															<button
																type="button"
																className="admin-icon-button"
																onClick={(clickEvent) => toggleVenueMenu(clickEvent, venue.id)}
																aria-haspopup="menu"
																aria-expanded={openMenuVenueId === venue.id}
																title="More actions"
															>
																<MoreHorizontal size={16} />
															</button>
															{openMenuVenueId === venue.id ? (
																<div
																	className="admin-menu admin-menu--fixed"
																	style={{
																		"--admin-menu-top": `${menuPosition.top}px`,
																		"--admin-menu-left": `${menuPosition.left}px`,
																		"--admin-menu-max-height": `${menuPosition.maxHeight}px`,
																	}}
																>
																	<button
																		type="button"
																		onClick={(menuEvent) => {
																			menuEvent.stopPropagation();
																			setOpenMenuVenueId(null);
																			openEditDialog(venue);
																		}}
																	>
																		<PencilLine size={14} />
																		Edit
																	</button>
																	<button
																		type="button"
																		onClick={(menuEvent) => {
																			menuEvent.stopPropagation();
																			setOpenMenuVenueId(null);
																			handleToggleVisibility(venue);
																		}}
																	>
																		{venue.isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
																		{venue.isVisible ? "Hide" : "Show"}
																	</button>
																	<button
																		type="button"
																		onClick={(menuEvent) => {
																			menuEvent.stopPropagation();
																			setOpenMenuVenueId(null);
																			setDialogState({ mode: "edit", venue });
																			setConfirmDeleteState({ isOpen: true, venue });
																		}}
																		className="venue-menu-item-danger"
																	>
																		<Trash2 size={14} />
																		Delete
																	</button>
																</div>
															) : null}
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
						<footer className="admin-row venue-table__footer">
							<span className="admin-muted">Page {currentPage} of {totalPages}</span>
							<span className="admin-pill is-blue">Showing {paginatedVenues.length} of {filteredVenues.length}</span>
							<div className="admin-actions">
								<button
									type="button"
									className="admin-button venue-pager-button"
									onClick={handlePreviousPage}
									disabled={currentPage <= 1}
								>
									<ChevronLeft size={16} /> Previous
								</button>
								<button
									type="button"
									className="admin-button venue-pager-button"
									onClick={handleNextPage}
									disabled={currentPage >= totalPages}
								>
									Next <ChevronRight size={16} />
								</button>
							</div>
						</footer>
					</div>
				)}
			</div>

			{dialogState.mode && (
				<div className="admin-dialog">
					<div className="admin-dialog__backdrop" onClick={closeDialog} />
					<div className="admin-dialog__panel venue-dialog-form">
						<div className="admin-dialog__header">
							<h2>
								{dialogState.mode === "create" ? "Create New Venue" : "Edit Venue"}
							</h2>
							<button type="button" onClick={closeDialog} className="admin-dialog__close">
								<X size={20} />
							</button>
						</div>

						<form onSubmit={handleSubmit}>
							<div className="admin-dialog__body">
								{dialogError && (
									<div className="venue-dialog-error">
										{dialogError}
									</div>
								)}

								<p className="venue-required-note">
									Fields marked with <span className="venue-required-asterisk">*</span> are required
								</p>

								<div className="admin-stack-md venue-form-sections">
									<div>
										<h3 className="venue-section-heading">
											<Building2 size={18} className="venue-section-icon" />
											Basic Information
										</h3>

										<div className="admin-field">
											<label>Venue Name *</label>
											<input
												type="text"
												value={formState.name}
												onChange={handleFieldChange("name")}
												placeholder="e.g., Main Conference Hall"
											/>
											{formErrors.name && <span className="admin-field-error">{formErrors.name}</span>}
										</div>

										<div className="venue-field-row">
											<div className="admin-field venue-field-half">
												<label>Type *</label>
												<select value={formState.type} onChange={handleFieldChange("type")}>
													{VENUE_TYPE_OPTIONS.map((opt) => (
														<option key={opt.value} value={opt.value}>
															{opt.label}
														</option>
													))}
												</select>
											</div>
											<div className="admin-field venue-field-half">
												<label>Capacity</label>
												<input
													type="number"
													value={formState.capacity}
													onChange={handleFieldChange("capacity")}
													placeholder="e.g., 100"
													min="0"
												/>
												{formErrors.capacity && <span className="admin-field-error">{formErrors.capacity}</span>}
											</div>
										</div>

										<div className="admin-field">
											<label>Max Pax (Capacity)</label>
											<input
												type="number"
												value={formState.maxPax}
												onChange={handleFieldChange("maxPax")}
												placeholder="e.g., 150"
												min="1"
											/>
											{formErrors.maxPax && <span className="admin-field-error">{formErrors.maxPax}</span>}
										</div>
									</div>

									<div>
										<h3 className="venue-section-heading">
											<DollarSign size={18} className="venue-section-icon" />
											Pricing Packages
										</h3>

										<div className="venue-pricing-grid">
											<div className="admin-field">
												<label>6 Hours Price</label>
												<input
													type="number"
													value={formState.price6hrs}
													onChange={handleFieldChange("price6hrs")}
													placeholder="e.g., 3000.00"
													step="0.01"
													min="0"
												/>
												{formErrors.price6hrs && <span className="admin-field-error">{formErrors.price6hrs}</span>}
											</div>
											<div className="admin-field">
												<label>12 Hours Price</label>
												<input
													type="number"
													value={formState.price12hrs}
													onChange={handleFieldChange("price12hrs")}
													placeholder="e.g., 5000.00"
													step="0.01"
													min="0"
												/>
												{formErrors.price12hrs && <span className="admin-field-error">{formErrors.price12hrs}</span>}
											</div>
											<div className="admin-field">
												<label>24 Hours Price</label>
												<input
													type="number"
													value={formState.price24hrs}
													onChange={handleFieldChange("price24hrs")}
													placeholder="e.g., 8000.00"
													step="0.01"
													min="0"
												/>
												{formErrors.price24hrs && <span className="admin-field-error">{formErrors.price24hrs}</span>}
											</div>
										</div>

										<div className="venue-discount-section">
											<h4 className="venue-discount-heading">
												<Tag size={16} className="venue-section-icon" />
												Discount
											</h4>
											<div className="venue-discount-controls">
												<select
													value={formState.discountMode === "custom" ? "custom" : formState.discountPercentage}
													onChange={(e) => {
														const val = e.target.value;
														if (val === "custom") {
															setFormState((prev) => ({ ...prev, discountMode: "custom", discountPercentage: "" }));
														} else {
															setFormState((prev) => ({ ...prev, discountMode: "preset", discountPercentage: val }));
														}
														if (formErrors.discountPercentage) {
															setFormErrors((prev) => ({ ...prev, discountPercentage: null }));
														}
													}}
												>
													<option value="0">0% (No Discount)</option>
													<option value="25">25%</option>
													<option value="50">50%</option>
													<option value="75">75%</option>
													<option value="100">100% (Free)</option>
													<option value="custom">Custom</option>
												</select>
												{formState.discountMode === "custom" && (
													<div className="venue-discount-custom-input">
														<input
															type="number"
															value={formState.discountPercentage}
															onChange={handleFieldChange("discountPercentage")}
															placeholder="e.g., 15"
															min="0"
															max="100"
															step="0.01"
														/>
														<span className="venue-discount-suffix">%</span>
													</div>
												)}
											</div>
											{formErrors.discountPercentage && <span className="admin-field-error">{formErrors.discountPercentage}</span>}
										</div>

										{Number(formState.discountPercentage) > 0 && (
											<div className="venue-discount-preview">
												<p className="venue-discount-preview-title">
													<Percent size={14} /> Discounted Prices Preview
												</p>
												<div className="venue-discount-preview-grid">
													{[
														{ label: "6 Hours", value: formState.price6hrs },
														{ label: "12 Hours", value: formState.price12hrs },
														{ label: "24 Hours", value: formState.price24hrs },
													].map(({ label, value }) => {
														const original = Number(value) || 0;
														const disc = Number(formState.discountPercentage) || 0;
														const discounted = original - (original * disc / 100);
														return (
															<div key={label} className="venue-discount-preview-item">
																<span className="venue-discount-preview-label">{label}</span>
																<span className="venue-discount-preview-original">₱{original.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
																<span className="venue-discount-preview-arrow">→</span>
																<span className="venue-discount-preview-final">₱{discounted.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
															</div>
														);
													})}
												</div>
											</div>
										)}
									</div>

									<div>
										<h3 className="venue-section-heading">
											<FileText size={18} className="venue-section-icon" />
											Description
										</h3>
										<div className="admin-field">
											<label>Short Description *</label>
											<textarea
												value={formState.shortDescription}
												onChange={handleFieldChange("shortDescription")}
												placeholder="Brief description for venue listings"
												rows="3"
											/>
											{formErrors.shortDescription && <span className="admin-field-error">{formErrors.shortDescription}</span>}
										</div>

										<div className="admin-field">
											<label>Detailed Description</label>
											<textarea
												value={formState.detailedDescription}
												onChange={handleFieldChange("detailedDescription")}
												placeholder="Full venue description with all details"
												rows="5"
											/>
										</div>

										<div className="admin-field">
											<label>Amenities</label>
											<textarea
												value={formState.amenities}
												onChange={handleFieldChange("amenities")}
												placeholder="e.g., WiFi, Air conditioning, Projector, Sound system"
												rows="2"
											/>
											<small className="venue-hint-text">
												Separate amenities with commas (e.g., WiFi, Air conditioning, Projector)
											</small>
										</div>
									</div>

									<div>
										<h3 className="venue-section-heading">
											<MapPin size={18} className="venue-section-icon" />
											Contact & Location
										</h3>
										<div className="admin-field">
											<label>Location</label>
											<input
												type="text"
												value={formState.location}
												onChange={handleFieldChange("location")}
												placeholder="e.g., Building A, 2nd Floor"
											/>
										</div>

										<div className="venue-field-row">
											<div className="admin-field venue-field-half">
												<label>Email</label>
												<input
													type="email"
													value={formState.email}
													onChange={handleFieldChange("email")}
													placeholder="venue@example.com"
												/>
											</div>
											<div className="admin-field venue-field-half">
												<label>Mobile Number</label>
												<input
													type="text"
													value={formState.mobileNumber}
													onChange={handleFieldChange("mobileNumber")}
													placeholder="e.g., +63 912 345 6789"
												/>
											</div>
										</div>

										<div className="admin-field">
											<label>Website Link</label>
											<input
												type="url"
												value={formState.link}
												onChange={handleFieldChange("link")}
												placeholder="https://example.com"
											/>
										</div>
									</div>

									<div>
										<h3 className="venue-section-heading">
											<ImageIcon size={18} className="venue-section-icon" />
											Images
										</h3>

										{formState.existingImages.length > 0 && (
											<div className="venue-existing-images">
												<p className="admin-muted venue-images-label">
													Existing Images ({formState.existingImages.length}):
												</p>
												<div className="venue-image-grid">
													{formState.existingImages.map((img) => (
														<div
															key={img.id}
															className="venue-image-preview"
														>
															<img
																src={resolveImageSrc(img.url)}
																alt="Venue"
																className={`venue-image-preview__img${img.isFeatured ? " is-featured" : ""}`}
															/>
															{img.isFeatured && (
																<span className="venue-featured-badge">
																	Featured
																</span>
															)}
															<button
																type="button"
																onClick={() => removeExistingImage(img.id)}
																className="venue-image-remove-btn"
															>
																<X size={14} />
															</button>
														</div>
													))}
												</div>
											</div>
										)}

										<div className="admin-field">
											<label>Upload Images {dialogState.mode === "create" && "*"}</label>
											<input
												key={`venue-images-${imageInputKey}`}
												type="file"
												accept={ACCEPTED_IMAGE_EXTENSIONS}
												multiple
												onChange={handleImageSelect}
												disabled={isSubmitting || isImageProcessing}
											/>
											<small className="admin-muted">
												Upload JPG, JPEG, PNG, or WEBP. Max 5MB per image before compression; uploaded at up to 1MB and 1920px max dimension.
											</small>
											{isImageProcessing ? <small className="admin-muted">Compressing images...</small> : null}
											{formErrors.images && <span className="admin-field-error">{formErrors.images}</span>}
										</div>

										{formState.imageFiles.length > 0 && (
											<div className="venue-new-uploads">
												<p className="admin-muted venue-images-label">
													New uploads ({formState.imageFiles.length}):
												</p>
												<div className="venue-uploads-grid">
													{formState.imageFiles.map((file, index) => (
														<div
															key={index}
															className="venue-image-preview"
														>
															<img
																src={URL.createObjectURL(file)}
																alt="Preview"
																className="venue-image-preview__img"
															/>
															<button
																type="button"
																onClick={() => removeUploadedImage(index)}
																className="venue-image-remove-btn"
															>
																<X size={14} />
															</button>
														</div>
													))}
												</div>
											</div>
										)}
									</div>

									<div className="venue-visibility-toggle">
										<div>
											<label className="venue-visibility-label">
												Visible to users
											</label>
											<small className="venue-hint-text">
												Uncheck to hide this venue from user-facing pages
											</small>
										</div>
										<label className="venue-toggle-switch">
											<input
												type="checkbox"
												checked={formState.isVisible}
												onChange={handleCheckboxChange("isVisible")}
											/>
											<span className={`venue-toggle-track${formState.isVisible ? " is-on" : ""}`} />
											<span className={`venue-toggle-knob${formState.isVisible ? " is-on" : ""}`} />
										</label>
									</div>
								</div>
							</div>

							<div className="admin-dialog__footer">
								{dialogState.mode === "edit" && (
									<button
										type="button"
										className="admin-button is-danger venue-btn-delete-left"
										onClick={openDeleteConfirm}
									>
										<Trash2 size={16} />
										Delete Venue
									</button>
								)}
								<button type="button" className="admin-button admin-button--ghost" onClick={closeDialog}>
									Cancel
								</button>
								<button
									type="submit"
									className="admin-button is-primary"
									disabled={isSubmitting || isImageProcessing}
								>
									{isImageProcessing
										? "Compressing..."
										: isSubmitting
										? "Uploading..."
										: dialogState.mode === "create"
										? "Create Venue"
										: "Save Changes"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{confirmDeleteState.isOpen && (
				<div className="admin-dialog">
					<div className="admin-dialog__backdrop" onClick={closeDeleteConfirm} />
					<div className="admin-dialog__panel venue-dialog-confirm">
						<div className="admin-dialog__header">
							<h2>Confirm Delete</h2>
							<button type="button" onClick={closeDeleteConfirm} className="admin-dialog__close">
								<X size={20} />
							</button>
						</div>
						<div className="admin-dialog__body">
							<div className="venue-danger-callout">
								<AlertTriangle size={24} className="venue-danger-icon" />
								<div>
									<p className="venue-danger-callout__title">
										Delete "{confirmDeleteState.venue?.name}"?
									</p>
									<p className="venue-danger-callout__desc admin-muted">
										This will soft-delete the venue. It will be hidden from all users but bookings will be preserved.
									</p>
								</div>
							</div>
						</div>
						<div className="admin-dialog__footer">
							<button type="button" className="admin-button" onClick={closeDeleteConfirm}>
								Cancel
							</button>
							<button
								type="button"
								className="admin-button is-danger"
								onClick={handleDelete}
								disabled={isDeleting}
							>
								{isDeleting ? "Deleting..." : "Delete Venue"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
